import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import requestClient from "../../utils/requestClient"; // Ensure requestClient is properly configured
import { motion } from "framer-motion"; 
import MarkdownMessage from "./MarkdownMessage"; 
import Cookies from 'js-cookie'


const ChatInput = forwardRef((props, ref) => {
  const { userType } = props;
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false); // 🟡 Tracks initial loading state

  const messagesEndRef = useRef(null); // 🟡 Reference to last message
  const scrollContainerRef = useRef(null); // 🟡 Reference to scroll container

  const loadMessages = async () => {
    setLoadingMessages(true); // Start loading state
    try {
      const response = await requestClient().get("/api/v2/assistant");
      if (response.data && response.data.messages) {
        setMessages(response.data.messages);  
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setLoadingMessages(false); // Stop loading state
      setTimeout(() => scrollToBottom(), 50);
    }
  };


  useImperativeHandle(ref, () => ({
    sendMessage,
  }));

  const sendMessage = async (overrideInput = null) => {
    const text = overrideInput ?? input.trim();
    const userMessage = { role: "user", content: text };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setWaiting(true);
    setTimeout(() => scrollToBottom(), 50);
  
    try {
      const csrftoken = Cookies.get('csrfmiddlewaretoken')

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v2/assistant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ message: text, user_type: userType }),
        credentials: "include" // ⬅️ send session cookie
      });
  
      if (!response.ok) {
        throw new Error("Failed to stream response");
      }
  
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
  
      let assistantMessage = { role: "assistant", content: "" };
      setMessages((prev) => [...prev, assistantMessage]);
  
      // Text is coming
      setWaiting(false);

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
  
        const chunk = decoder.decode(value, { stream: true });
  
        // Assuming your server sends: data: {"content": "Hello"}\n\n
        const lines = chunk.split("\n").filter(Boolean);
        for (const line of lines) {
          const data = JSON.parse(line);
          if (data.content) {
            assistantMessage.content += data.content;

            // Update last assistant message in the UI
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { ...assistantMessage };
              return updated;
            });

            scrollToBottom();
          } 
        }
      }
  
    } catch (err) {
      console.error("Streaming error:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await requestClient().delete("/api/v2/assistant");
      setMessages([]); // Reset UI messages
    } catch (error) {
      console.error("Error clearing chat:", error);
    }
  };

    // 🟡 Function to scroll to the bottom of the chat
    const scrollToBottom = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    };

    useEffect(() => {
        loadMessages();
    }, []);

  return (
    <>
      <div ref={scrollContainerRef} className="h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
        {/* 🟡 Show loading indicator while fetching messages */}
        {loadingMessages ? (
          <p className="text-sm text-center">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-center">No messages yet.</p>
        ) : (
          messages.map((msg, index) => (
            <motion.div 
                key={index}
                initial={{ opacity: 0, x: msg.role === "user" ? 30 : -30 }} // 🟡 Slide from side
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.3 }} // 🟢 Staggered effect
                className={`mb-3 ${msg.role === "user" ? "text-right ml-8" : "text-left mr-8"}`}
            >
              <MarkdownMessage className={`chat-message px-3 py-2 rounded-lg prose prose-purple inline-block shadow ${msg.role === "user" ? "bg-purple-light-alt" : "bg-white"}`} content={msg.content} />
            </motion.div>
          ))
        )}
        {waiting && (<motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.3 }}
            className={`mb-3 text-left`}
        >
            <span className={`px-3 py-2 rounded-lg inline-block bg-purple text-white`}>...</span>
        </motion.div>)}
        {/* 🟡 Invisible div to scroll to */}
        <div ref={messagesEndRef}></div>
      </div>

      <div className="mt-4 flex gap-2 flex-col">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          className="flex-1 p-2 border rounded-lg outline-none placeholder:text-black placeholder:italic"
          placeholder="Ask anything about wellbeing..."
        />
        <button onClick={() => sendMessage()} className="btn btn-purple" disabled={loading}>
          {loading ? "Sending..." : "Send"}
        </button>
        <button onClick={clearChat} className="btn btn-invert w-full">
          Clear Chat
        </button>
      </div>
    </>
  );
});

export default ChatInput;
